import { PageLayout } from '@app/pages/layout/PageLayout'
import { useQuery } from '@based/sdk/react'
import { Blog } from '@schema/blog'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import Column from '@app/components/layout/Column'
import { styled } from 'inlines'
import { Author } from '@app/components/blocks/author/Author'
import useMediaQuery from '@app/util/useMediaQuery'
import { pages } from '@app/util/links'
import { useRoute } from 'wouter'
import { Stories } from '@schema/stories'
import { News } from '@schema/news'
import { Image } from '@app/components/core/image/Image'
import { expandImage } from '@app/types/StoredImage'
import { useTranslations } from '@i18n/useLanguage'
import useDateFormat from '@app/util/useDateFormat'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { buildArticleJsonLd } from '@app/util/seo/buildArticleJsonLd'
import { useRootDomain } from '@app/util/hooks/useRootDomain'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'

export type BlogItemPageProps = {
  blogId: string
}

const sharedParagraphStyles = {
  maxWidth: '50rem',
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '100%',
}

const blogTypeToQueryMapping = {
  verhalen: 'view-stories',
  news: 'view-news',
  blog: 'view-blog',
}

export const BlogItemPage = ({ blogId }: BlogItemPageProps) => {
  const [, params] = useRoute<{ blogType: string }>(
    pages.blog(':blogType', ':blogId'),
  )
  const blogType = params?.blogType as 'verhalen' | 'news' | 'blog'
  const isMobile = useMediaQuery()
  const t = useTranslations()
  const dateFormat = useDateFormat()
  const queryName = blogTypeToQueryMapping[blogType]
  const itemQuery = useQuery(queryName ?? null, { id: blogId })
  const item: Blog | Stories | News | undefined = itemQuery.data
  const rootDomain = useRootDomain()

  const pageStatus = getEntityPageStatus(itemQuery.loading, item)

  if (pageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (pageStatus === 'loading') {
    return (
      <PageLayout gap="2rem">
        <PageSeo />
        {t.app.blogPage.loading}
      </PageLayout>
    )
  }

  const { src, altText } = expandImage(item.images?.[0], item.title)

  return (
    <>
      <PageLayout gap="0rem">
        <PageSeo
          pageTitle={item.title}
          description={item.intro}
          image={item.images?.[0]}
          type="article"
          jsonLd={buildArticleJsonLd({
            headline: item.title,
            description: item.intro,
            url: `${rootDomain}${pages.blog(blogType, blogId)}`,
            imageUrl: src,
            datePublished: new Date(item.publishedAt).toISOString(),
            authorName: `${item.author.firstName} ${item.author.lastName}`,
          })}
        />
        <Column
          style={{
            ...sharedParagraphStyles,
            marginBottom: isMobile ? '2rem' : '4rem',
            marginTop: isMobile ? '2rem' : '2rem',
          }}
        >
          <div>
            <Text.Small style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
              {dateFormat(new Date(item.publishedAt))}
            </Text.Small>
            <Title.Page styledAsLevel={isMobile ? 2 : 1} style={{ margin: 0 }}>
              {item.title}
            </Title.Page>
          </div>
          <Text.Large>{item.intro}</Text.Large>
          <Author name={`${item.author.firstName} ${item.author.lastName}`} />
        </Column>

        {src && (
          <Image
            src={src}
            alt={altText}
            style={{
              borderRadius: '1.25rem',
              marginBottom: '2rem',
              maxHeight: isMobile ? '12rem' : '35rem',
              width: '100%',
              objectFit: 'cover',
            }}
          />
        )}

        <styled.div style={sharedParagraphStyles}>
          <SafeHtml html={item.content} />
        </styled.div>
      </PageLayout>
    </>
  )
}
